import { create } from 'zustand';
import { toast } from 'sonner';
import { useRecordingSessionStore } from './useRecordingSessionStore';
import { useSynthJobsStore } from './useSynthJobsStore';

interface PhoneScribeState {
  /** True while the phone-source flow is active (modal visible OR minimized). */
  active: boolean;
  /** When true, the modal is collapsed to a corner pill so the doctor can
   *  navigate the EMR while the phone keeps recording. */
  minimized: boolean;
  /** Forwarded to the modal so the SOAP synth can be tied to the right patient.
   *  Optional — ad-hoc capture without a patient is supported. */
  patientId?: string;
  recordingSessionId?: string;

  open: (init?: { patientId?: string; recordingSessionId?: string }) => void;
  minimize: () => void;
  restore: () => void;
  close: () => void;
}

/**
 * Global state for the "Record from phone" surface so a clinician can
 * minimize the modal to a pill, navigate around the EMR while the phone
 * keeps streaming audio, and pop the modal back open from any page.
 *
 * The actual scribe session lifecycle (start-session / end-recording /
 * socket events) lives inside RecordOnPhoneModal — this store only owns
 * the visibility/UX state. Closing here (or hitting Stop in the modal)
 * fires the appropriate socket teardown.
 */
export const usePhoneScribeStore = create<PhoneScribeState>((set) => ({
  active: false,
  minimized: false,
  patientId: undefined,
  recordingSessionId: undefined,

  open: (init) => {
    // Phone-as-mic and the desktop RecordingDock share the same
    // ai-gateway socket. A second `scribe.start-session` on that socket
    // silently overwrites the dock's session config server-side — the
    // dock's mic chunks then get bucketed under the modal's session
    // (and worse, an in-flight synth from the dock's session would
    // commit its result against the modal's recordingSessionId). So
    // we block until the dock's session is gone.
    //
    // Surface the right message:
    //   - Still capturing/paused → "stop first" with the action they need.
    //   - Synth in flight (Complete already pressed, soap-result hasn't
    //     landed yet) → "wait for the note, then retry" — they don't need
    //     to do anything, the dock will clear itself when synth completes.
    const dockSession = useRecordingSessionStore.getState().session;
    if (dockSession) {
      const synthJob = useSynthJobsStore.getState().jobs[dockSession.id];
      const synthInFlight =
        synthJob?.status === 'running' ||
        dockSession.stages?.synth === 'in_flight';
      if (synthInFlight) {
        toast(
          "The previous note is still generating. We'll let you know when it's ready — start phone capture after that.",
          { duration: 6000 },
        );
      } else {
        toast.error(
          'Stop the in-progress recording before starting phone capture.',
        );
      }
      return;
    }
    set({
      active: true,
      minimized: false,
      patientId: init?.patientId,
      recordingSessionId: init?.recordingSessionId,
    });
  },
  minimize: () => set({ minimized: true }),
  restore: () => set({ minimized: false }),
  close: () =>
    set({
      active: false,
      minimized: false,
      patientId: undefined,
      recordingSessionId: undefined,
    }),
}));
